import {
  CapacityIcon,
  HotelLocation,
  ImageIcon,
  CloseIcon,
} from "../../icons/icons";
import { serverUrl } from "../../../config";

require("./index.css").toString();

const NUMERIC_FIELDS = [
  { key: "numOfBedrooms", label: "GUEST ROOMS", displayLabel: "Guest Rooms" },
  {
    key: "numOfMeetingRooms",
    label: "MEETING ROOMS",
    displayLabel: "Meeting Rooms",
  },
  { key: "maxCapacity", label: "MAX CAP", displayLabel: "Max Cap" },
];

const MIN_SELECTED = 2;
const MAX_SELECTED = 4;

function decodeHTML(str) {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
}

export default class AtAGlance {
  static get toolbox() {
    return {
      icon: CapacityIcon,
      title: "At A Glance",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;
    this.config = config;

    const rawOverrides = data.imageOverrides;

    this._data = {
      emmaId: data.emmaId || "",
      hotelName: data.hotelName || "",
      title: data.title || "",
      description: data.description || "",
      selectedItems: data.selectedItems || [],
      apiData: data.apiData || null,
      // Ensure imageOverrides is always an object to avoid errors when accessing properties, even if the saved data is malformed. Editorjs maybe mishandling empty objects into an array.
      imageOverrides:
        rawOverrides &&
        typeof rawOverrides === "object" &&
        !Array.isArray(rawOverrides)
          ? rawOverrides
          : {},
      heroOpacity: data.heroOpacity != null ? data.heroOpacity : 0.7,
    };

    this._tempData = {};

    this._CSS = {
      // standard css
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      blockBtnLargeWide: "wide",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      emphasis: "emphasis",
      save: "save",
      clear: "clear",
      cancel: "cancel",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      tuneContent: "editor-block-tune-content-container",
      slider: "cdx-opacity-slider",
      sliderLabel: "cdx-opacity-slider-label",

      editorFont: "editor-font",
      editorBlockToolbar: "editor-block-toolbar",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };
  }

  render() {
    this._fetchAllHotels(); // For testing purposes - fetch and log all hotels on modal open to verify API connectivity and data structure. Can be removed later.
    const css = this._CSS;
    this._container = document.createElement("div");
    this._container.classList.add("at-a-glance-container");

    // hidden input to prevent empty block creation
    const hiddenInput = document.createElement("input");
    hiddenInput.setAttribute(
      "style",
      "visibility:hidden;width:0;height:0;padding:0;margin:0;border:0;position:absolute",
    );
    this._container.appendChild(hiddenInput);

    this._renderConfigured();

    // Configure button + modal (edit mode only)
    if (!this.readOnly) {
      this._configureBtn = document.createElement("button");
      this._configureBtn.classList.add(
        "at-a-glance-configure-btn",
        css.blockBtn,
        css.blockBtnLarge,
        css.blockBtnLargeWide,
      );
      this._configureBtn.textContent = "Configure";
      this._configureBtn.addEventListener("click", () => this._showModal());
      this._container.appendChild(this._configureBtn);

      this._modal = this._buildModal();
      this._container.appendChild(this._modal);
    }

    return this._container;
  }

  renderSettings() {
    const wrapper = document.createElement("div");
    const label = document.createElement("label");
    const content = document.createElement("div");
    const slider = document.createElement("input");
    const sliderLabel = document.createElement("label");

    wrapper.classList.add(
      this._CSS.wrapper,
      this._CSS.tuneWrapper,
      "cdx-color-picker-settings",
    );
    label.classList.add(this._CSS.tuneTitle);
    content.classList.add(this._CSS.tuneContent);
    slider.classList.add(this._CSS.slider, this._CSS.tuneInput);
    sliderLabel.classList.add(this._CSS.sliderLabel, this._CSS.tuneTitle);

    slider.type = "range";
    slider.min = "0";
    slider.max = "1";
    slider.step = "0.01";
    slider.value = this._data.heroOpacity;
    slider.id = "overlayOpacitySlider";
    slider.name = "overlayOpacitySlider";
    sliderLabel.innerText = `Hero Image Opacity: ${Math.floor(this._data.heroOpacity * 100)}%`;

    slider.addEventListener("input", (e) => {
      const value = parseFloat(e.target.value);
      this._data.heroOpacity = value;
      sliderLabel.innerText = `Hero Image Opacity: ${Math.floor(value * 100)}%`;

      const imgTarget = this._container?.querySelector(".at-a-glance-hero");
      if (imgTarget) {
        imgTarget.style.opacity = value;
      }

      this.save();
    });

    wrapper.appendChild(sliderLabel);
    wrapper.appendChild(slider);

    return wrapper;
  }

  _renderHotelSearch() {
    const css = this._CSS;
    const searchContainer = document.createElement("div");
    const hotelNameLabel = document.createElement("label");
    const resultsBox = document.createElement("div");
    const hotelNameInputContainer = document.createElement("div");
    const clearBtn = document.createElement("button");
    this.hotelNameInput = document.createElement("input");

    searchContainer.classList.add(
      "aag-hotel-search-container",
      "form-group-wrapper",
    );
    hotelNameLabel.classList.add(css.editorBlockModalLabel);
    resultsBox.classList.add("aag-hotel-search-results");
    hotelNameInputContainer.classList.add("aag-hotel-search-input-container");
    this.hotelNameInput.classList.add(
      css.editorBlockModalInput,
      "aag-hotel-search-input",
    );
    clearBtn.classList.add("aag-search-clear-btn");

    clearBtn.innerHTML = CloseIcon;

    clearBtn.addEventListener("click", () => {
      this._clearSearchInputs();
      resultsBox.style.display = "none";
    });

    hotelNameLabel.textContent = "Hotel Name:";
    this.hotelNameInput.setAttribute("autocomplete", "off");
    this.hotelNameInput.placeholder = "E.G. Radisson Blu Hotel Frankfurt";
    this.hotelNameInput.value = this._data.hotelName;
    resultsBox.style.display = "none";

    hotelNameInputContainer.appendChild(this.hotelNameInput);
    hotelNameInputContainer.appendChild(clearBtn);
    searchContainer.appendChild(hotelNameLabel);
    searchContainer.appendChild(hotelNameInputContainer);
    searchContainer.appendChild(resultsBox);

    const fuzzyScore = (query, target) => {
      const q = query.toLowerCase();
      const t = target.toLowerCase();
      let qi = 0,
        score = 0,
        consecutive = 0,
        lastMatch = -1;
      for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        if (t[ti] === q[qi]) {
          score += 1 + consecutive * 2;
          consecutive = lastMatch === ti - 1 ? consecutive + 1 : 0;
          lastMatch = ti;
          qi++;
        }
      }
      return qi < q.length ? -1 : score - t.length * 0.01;
    };

    const renderResults = (query) => {
      resultsBox.innerHTML = "";
      if (!query) {
        resultsBox.style.display = "none";
        return;
      }

      const scored = this._allHotels
        .map((h) => ({ ...h, score: fuzzyScore(query, h.name) }))
        .filter((h) => h.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);

      if (!scored.length) {
        resultsBox.style.display = "none";
        return;
      }

      scored.forEach((h) => {
        const item = document.createElement("div");
        item.classList.add("aag-hotel-search-item");
        item.textContent = h.name;
        item.addEventListener("mousedown", (e) => {
          e.preventDefault(); // prevent input blur before click registers
          this.hotelNameInput.value = h.name;
          this._emmaInput.value = h.tmsHotelCode;
          this._applyBtn.disabled = false;
          resultsBox.style.display = "none";
        });
        resultsBox.appendChild(item);
      });

      resultsBox.style.display = "flex";
      resultsBox.scrollTop = 0;
    };

    let debounceTimer;
    this.hotelNameInput.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(
        () => renderResults(this.hotelNameInput.value.trim()),
        150,
      );
    });

    this.hotelNameInput.addEventListener("blur", () => {
      resultsBox.style.display = "none";
    });

    return searchContainer;
  }

  _renderEmmaSearch() {
    const css = this._CSS;
    const searchContainer = document.createElement("div");
    const emmaLabel = document.createElement("label");
    const resultsBox = document.createElement("div");
    const emmaInputContainer = document.createElement("div");
    const clearBtn = document.createElement("button");
    this._emmaInput = document.createElement("input");

    searchContainer.classList.add(
      "aag-emma-search-container",
      "form-group-wrapper",
    );
    emmaLabel.classList.add(css.editorBlockModalLabel);
    resultsBox.classList.add("aag-emma-search-results");
    emmaInputContainer.classList.add("aag-emma-search-input-container");
    clearBtn.classList.add("aag-search-clear-btn");
    this._emmaInput.classList.add(
      css.editorBlockModalInput,
      "aag-emma-search-input",
    );

    this._emmaInput.type = "text";
    this._emmaInput.placeholder = "e.g. DEFRA1";
    this._emmaInput.value = this._data.emmaId;
    this._emmaInput.setAttribute("autocomplete", "off");
    clearBtn.innerHTML = CloseIcon;

    emmaLabel.textContent = "EMMA ID:";
    resultsBox.style.display = "none";

    clearBtn.addEventListener("click", () => {
      this._clearSearchInputs();
      resultsBox.style.display = "none";
    });

    searchContainer.appendChild(emmaLabel);
    searchContainer.appendChild(emmaInputContainer);
    emmaInputContainer.appendChild(this._emmaInput);
    emmaInputContainer.appendChild(clearBtn);
    searchContainer.appendChild(resultsBox);

    const fuzzyScore = (query, target) => {
      const q = query.toLowerCase();
      const t = target.toLowerCase();
      let qi = 0,
        score = 0,
        consecutive = 0,
        lastMatch = -1;
      for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        if (t[ti] === q[qi]) {
          score += 1 + consecutive * 2;
          consecutive = lastMatch === ti - 1 ? consecutive + 1 : 0;
          lastMatch = ti;
          qi++;
        }
      }
      return qi < q.length ? -1 : score - t.length * 0.01;
    };

    const renderResults = (query) => {
      resultsBox.innerHTML = "";
      if (!query) {
        resultsBox.style.display = "none";
        return;
      }

      const scored = this._allHotels
        .map((h) => ({ ...h, score: fuzzyScore(query, h.tmsHotelCode) }))
        .filter((h) => h.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);

      if (!scored.length) {
        resultsBox.style.display = "none";
        return;
      }

      scored.forEach((h) => {
        const item = document.createElement("div");
        item.classList.add("aag-emma-search-item");
        item.textContent = `${h.tmsHotelCode} - ${h.name}`;
        item.addEventListener("mousedown", (e) => {
          e.preventDefault(); // prevent input blur before click registers
          this._emmaInput.value = h.tmsHotelCode;
          this.hotelNameInput.value = h.name;
          resultsBox.style.display = "none";
          this._applyBtn.disabled = false;
        });
        resultsBox.appendChild(item);
      });

      resultsBox.style.display = "flex";
      resultsBox.scrollTop = 0;
    };

    let debounceTimer;
    this._emmaInput.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(
        () => renderResults(this._emmaInput.value.trim()),
        150,
      );
    });

    this._emmaInput.addEventListener("blur", () => {
      resultsBox.style.display = "none";
    });

    return searchContainer;
  }

  _clearSearchInputs() {
    if (this.hotelNameInput) {
      this.hotelNameInput.value = "";
    }
    if (this._emmaInput) {
      this._emmaInput.value = "";
    }
    this._applyBtn.disabled = true;
  }

  _getHotelDetails() {
    const api = this._data.apiData;
    if (!api) return null;

    const hotelInfo = api?.hotelInfo?.collection?.[0];
    const content = hotelInfo?.content || {};
    const picked = api?.assets?.picked || {};

    const location =
      hotelInfo?.metadata?.location?.values?.[0]?.description || "";

    return {
      name: decodeHTML(this._data.hotelName || ""),
      title:
        this._data.title || decodeHTML(content?.text?.content?.title || ""),
      description:
        this._data.description ||
        decodeHTML(content?.text?.content?.shortDescription || ""),
      city: decodeHTML(hotelInfo?.metadata?.city?.description || ""),
      country: decodeHTML(hotelInfo?.metadata?.country?.description || ""),
      brand: decodeHTML(hotelInfo?.metadata?.brand?.description || ""),
      brandCode: hotelInfo?.metadata?.brand?.key || "",
      location: decodeHTML(location),
      url: hotelInfo?.linkUrl || "",
      heroImage:
        this._data.imageOverrides.hero ||
        content?.image?.binaryContent?.url ||
        "",
      images: {
        guestRoom:
          this._data.imageOverrides.guestRoom ||
          picked?.guestRoom?.links?.cdnLinks?.[0] ||
          "",
        meetingRoom:
          this._data.imageOverrides.meetingRoom ||
          picked?.meetingRoom?.links?.cdnLinks?.[0] ||
          "",
        restaurant:
          this._data.imageOverrides.restaurant ||
          picked?.restaurant?.links?.cdnLinks?.[0] ||
          "",
        gym:
          this._data.imageOverrides.gym ||
          picked?.gym?.links?.cdnLinks?.[0] ||
          "",
      },
    };
  }

  _createReplaceBtn(imageSlot) {
    const css = this._CSS;
    const IMAGE_SLOT_FILE_INDEX = {
      hero: 0,
      guestRoom: 1,
      meetingRoom: 2,
      restaurant: 3,
      gym: 4,
    };

    const btn = document.createElement("button");
    btn.classList.add(
      "aag-replace-btn",
      css.blockBtn,
      css.blockBtnSmall,
      css.xxlIcon,
    );
    btn.innerHTML = ImageIcon;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._pendingImageSlot = imageSlot;
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);

      // Get file restrictions for this image slot from layout options
      const fileIndex = IMAGE_SLOT_FILE_INDEX[imageSlot];

      const layoutFiles = this.config?.layoutFiles;
      const slotFiles =
        Array.isArray(layoutFiles) && layoutFiles[fileIndex]
          ? layoutFiles[fileIndex]
          : undefined;

      this.config.showMediaLibrary({
        details: block,
        data: this._data,
        bundle: "image",
        imageSlot: imageSlot,
        slideFiles: slotFiles,
      });
    });
    return btn;
  }

  _createResetBtn(imageSlot) {
    const css = this._CSS;
    const btn = document.createElement("button");
    btn.classList.add(
      "aag-reset-btn",
      css.blockBtn,
      css.blockBtnSmall,
      css.xlIcon,
    );
    btn.title = "Reset to original";
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" width="16" height="16"><path d="M440-122q-121-15-200.5-105.5T160-440q0-66 26-126.5T260-672l57 57q-38 34-57.5 79T240-440q0 88 56 155.5T440-202v80Zm80 0v-80q87-16 143.5-83T720-440q0-100-70-170t-170-70h-3l44 44-56 56-140-140 140-140 56 56-44 44h3q134 0 227 93t93 227q0 121-79.5 211.5T520-122Z" fill="currentColor"/></svg>`;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      delete this._data.imageOverrides[imageSlot];
      this._renderConfigured();
      if (this.block?.dispatchChange) {
        this.block.dispatchChange();
      }
    });

    if (!this._data.imageOverrides[imageSlot]) {
      btn.style.display = "none";
    }

    return btn;
  }

  onMediaSelected(media) {
    if (!this._pendingImageSlot || !media?.field_media_image) return;
    this._data.imageOverrides[this._pendingImageSlot] = media.field_media_image;
    this._pendingImageSlot = null;
    this._renderConfigured();
    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  _renderConfigured() {
    if (this._contentEl) {
      this._contentEl.remove();
    }

    this._contentEl = document.createElement("div");
    this._contentEl.classList.add("at-a-glance-content-container");

    const hotel = this._getHotelDetails();

    if (!hotel && this._data.selectedItems.length === 0) {
      const placeholder = document.createElement("div");
      placeholder.style.cssText = "color:#999;font-size:16px;";
      placeholder.textContent = 'Click "Configure" to set up At A Glance';
      this._contentEl.appendChild(placeholder);
      this._container.appendChild(this._contentEl);
      return;
    }

    // Left column: hero image + overlay (brand logo, title, description)
    const leftCol = document.createElement("div");
    leftCol.classList.add("at-a-glance-left");

    if (hotel?.heroImage) {
      const heroImg = document.createElement("img");
      heroImg.src = hotel.heroImage;
      heroImg.classList.add("at-a-glance-hero");
      heroImg.style.opacity = this._data.heroOpacity;
      leftCol.appendChild(heroImg);

      if (!this.readOnly && this.config?.showMediaLibrary) {
        leftCol.appendChild(this._createReplaceBtn("hero"));
        leftCol.appendChild(this._createResetBtn("hero"));
      }
    }

    if (hotel) {
      const overlay = document.createElement("div");
      overlay.classList.add("content_block", "hotelTextOverlay");

      const overlayContent = document.createElement("div");
      overlayContent.classList.add("hotel-text-overlay-content-container");

      if (hotel.brandCode && this.config?.brandLogos?.[hotel.brandCode]) {
        const logoEl = document.createElement("div");
        logoEl.classList.add("hotel-logo-container");
        logoEl.innerHTML = this.config.brandLogos[hotel.brandCode];
        overlayContent.appendChild(logoEl);
      }

      const titleEl = document.createElement("h4");
      titleEl.classList.add("overlay-heading");
      titleEl.innerHTML = hotel.title || "";
      if (!this.readOnly) {
        titleEl.contentEditable = "true";
        titleEl.dataset.placeholder = "Overlay tagline";
        titleEl.addEventListener("input", () => {
          this._data.title = titleEl.innerHTML;
        });
      }
      overlayContent.appendChild(titleEl);

      const descEl = document.createElement("p");
      descEl.classList.add("overlay-body");
      descEl.innerHTML = hotel.description || "";
      if (!this.readOnly) {
        descEl.contentEditable = "true";
        descEl.dataset.placeholder = "Overlay description";
        descEl.addEventListener("input", () => {
          this._data.description = descEl.innerHTML;
        });
      }
      overlayContent.appendChild(descEl);

      overlay.appendChild(overlayContent);
      leftCol.appendChild(overlay);
    }

    this._contentEl.appendChild(leftCol);

    // Right column: image grid, hotel info, capacity/services, footer
    const rightCol = document.createElement("div");
    rightCol.classList.add("at-a-glance-right");

    // Image grid (2×2)
    if (hotel) {
      const thumbs = Object.entries(hotel.images).filter(([, url]) => url);
      if (thumbs.length > 0) {
        const imageGrid = document.createElement("div");
        imageGrid.classList.add("at-a-glance-image-grid");

        thumbs.forEach(([key, url]) => {
          const container = document.createElement("div");
          container.classList.add("img-container");
          const img = document.createElement("img");
          img.src = url;
          img.alt = key;
          container.appendChild(img);

          if (!this.readOnly && this.config?.showMediaLibrary) {
            container.appendChild(this._createReplaceBtn(key));
            container.appendChild(this._createResetBtn(key));
          }

          imageGrid.appendChild(container);
        });

        rightCol.appendChild(imageGrid);
      }
    }

    // Hotel info (location header: pin, country, city, name)
    if (hotel) {
      const headingContainer = document.createElement("div");
      headingContainer.classList.add("hotel-heading-container");

      const headingContent = document.createElement("div");
      headingContent.classList.add("hotel-heading-content-container");

      const pinIcon = document.createElement("div");
      pinIcon.classList.add("location-pin-icon");
      pinIcon.innerHTML = HotelLocation;
      headingContent.appendChild(pinIcon);

      if (hotel.country) {
        const countryEl = document.createElement("h6");
        countryEl.classList.add("hotel-country");
        countryEl.textContent = hotel.country;
        headingContent.appendChild(countryEl);
      }

      if (hotel.city) {
        const cityEl = document.createElement("h6");
        cityEl.classList.add("hotel-city");
        cityEl.textContent = hotel.city;
        headingContent.appendChild(cityEl);
      }

      if (hotel.name) {
        const nameEl = document.createElement("h6");
        nameEl.classList.add("hotel-name");
        nameEl.textContent = hotel.name;
        headingContent.appendChild(nameEl);
      }

      headingContainer.appendChild(headingContent);
      rightCol.appendChild(headingContainer);
    }

    // Capacity / services items
    if (this._data.selectedItems.length > 0) {
      const capacityContainer = document.createElement("div");
      capacityContainer.classList.add("hotel-capacity-container");

      const capacityContent = document.createElement("div");
      capacityContent.classList.add("hotel-capacity-content-container");

      this._data.selectedItems.forEach((item) => {
        const group = document.createElement("div");
        group.classList.add("hotel-capacity-group");

        if (item.type === "numeric") {
          const val = document.createElement("div");
          val.classList.add("hotel-capacity-value");
          val.textContent =
            item.value != null ? Number(item.value).toLocaleString() : "—";
          group.appendChild(val);
        } else {
          const check = document.createElement("div");
          check.classList.add("hotel-capacity-value");
          check.textContent = "✔";
          group.appendChild(check);
        }

        const label = document.createElement("div");
        label.classList.add("hotel-capacity-label");
        const words = item.label.toUpperCase().split(" ");
        if (words.length >= 2) {
          label.innerHTML = words[0] + "<br>" + words.slice(1).join(" ");
        } else {
          label.textContent = item.label.toUpperCase();
        }
        group.appendChild(label);

        capacityContent.appendChild(group);
      });

      capacityContainer.appendChild(capacityContent);
      rightCol.appendChild(capacityContainer);
    }

    // Footer (location + hotel link)
    if (hotel) {
      const footerContainer = document.createElement("div");
      footerContainer.classList.add("hotel-footer-container");

      const footerContent = document.createElement("div");
      footerContent.classList.add("hotel-footer-content-container");

      if (hotel.location) {
        const locContainer = document.createElement("div");
        locContainer.classList.add("hotel-footer-location-container");

        const locLabel = document.createElement("p");
        locLabel.classList.add("hotel-footer-location-label");
        locLabel.textContent = "LOCATION:";
        locContainer.appendChild(locLabel);

        const locValue = document.createElement("p");
        locValue.classList.add("hotel-footer-location");
        locValue.textContent = hotel.location;
        locContainer.appendChild(locValue);

        footerContent.appendChild(locContainer);
      }

      if (hotel.url) {
        const link = document.createElement("a");
        link.classList.add("hotel-footer-url");
        link.href = `https://radissonhotels.com${hotel.url}`;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = "VISIT HOTEL WEBSITE";
        footerContent.appendChild(link);
      }

      footerContainer.appendChild(footerContent);
      rightCol.appendChild(footerContainer);
    }

    this._contentEl.appendChild(rightCol);
    this._container.appendChild(this._contentEl);
  }

  _buildModal() {
    const css = this._CSS;
    const modal = document.createElement("div");
    const backdrop = document.createElement("div");
    const or = document.createElement("span");
    or.classList.add("aag-or-separator");
    or.textContent = "OR";

    modal.classList.add("at-a-glance-modal", css.editorBlockModal);
    backdrop.classList.add(
      "at-a-glance-modal-backdrop",
      css.editorBlockModalBackdrop,
    );
    backdrop.addEventListener("click", () => this._hideModal());

    const inner = document.createElement("div");
    inner.classList.add("at-a-glance-modal-inner");

    const heading = document.createElement("h4");
    heading.classList.add(css.editorBlockModalTitle);
    heading.textContent = "Configure template";

    // EMMA ID row (label, input + fetch button)
    const modalPrompt = document.createElement("label");
    const emmaRow = document.createElement("div");

    emmaRow.classList.add("at-a-glance-emma-row");
    modalPrompt.classList.add(css.editorBlockModalText, "at-a-glance-modal-prompt");

    modalPrompt.textContent =
      "Enter hotel name, or EMMA ID, then click 'Load Hotel Data'. Once data is loaded, you can choose which hotel features you would like to display (max. 4).";

    this._fetchBtn = document.createElement("button");
    this._fetchBtn.classList.add(
      "at-a-glance-fetch-btn",
      css.blockBtn,
      css.blockBtnLarge,
      css.blockBtnLargeWide,
      css.emphasis,
    );
    this._fetchBtn.textContent = "Load Hotel Data";
    this._fetchBtn.addEventListener("click", () => this._fetchData());

    emmaRow.appendChild(this._renderHotelSearch());
    emmaRow.appendChild(or);
    emmaRow.appendChild(this._renderEmmaSearch());
    emmaRow.appendChild(this._fetchBtn);

    // Status message
    this._statusEl = document.createElement("div");
    this._statusEl.classList.add("at-a-glance-status");

    // Selection area (hidden until data fetched)
    this._selectionArea = document.createElement("div");
    this._selectionArea.style.display = "none";

    // Actions
    const actions = document.createElement("div");
    actions.classList.add("at-a-glance-modal-actions");

    const cancelBtn = document.createElement("button");
    cancelBtn.classList.add(
      "aag-cancel-btn",
      css.blockBtn,
      css.blockBtnLarge,
      css.blockBtnLargeWide,
      css.cancel,
    );
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", () => this._hideModal());

    this._applyBtn = document.createElement("button");
    this._applyBtn.classList.add(
      "aag-apply-btn",
      css.blockBtn,
      css.blockBtnLarge,
      css.blockBtnLargeWide,
      css.save,
    );
    this._applyBtn.textContent = "Apply";
    this._applyBtn.addEventListener("click", () => this._applySettings());

    actions.appendChild(cancelBtn);
    actions.appendChild(this._applyBtn);

    inner.appendChild(heading);
    inner.appendChild(modalPrompt);
    inner.appendChild(emmaRow);
    inner.appendChild(this._statusEl);
    inner.appendChild(this._selectionArea);
    inner.appendChild(actions);

    modal.appendChild(backdrop);
    modal.appendChild(inner);

    return modal;
  }

  async _fetchData() {
    const emmaId = this._emmaInput.value.trim();
    if (!emmaId) {
      this._statusEl.textContent = "Please enter a valid input.";
      this._statusEl.classList.add("error");
      return;
    }

    this._fetchBtn.disabled = true;
    this._fetchBtn.textContent = "Fetching...";
    this._statusEl.textContent = "";
    this._statusEl.classList.remove("error");

    try {
      const url = `${serverUrl}/api/v2/rhg/map?endpoint=at-a-glance&emma=${encodeURIComponent(emmaId)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this._data.selectedItems = [];

      const apiData = await res.json();
      this._tempData.apiData = apiData;
      this._tempData.emmaId = emmaId;
      if (
        apiData?.hotelInfo?.collection?.length > 0 &&
        apiData?.hotelInfo?.collection?.length > 0
      ) {
        // Extract hotel name from hotelInfo or hotelRoomInfo
        const hotelInfo = apiData?.hotelInfo?.collection?.[0];
        const roomInfo = apiData?.hotelRoomInfo?.collection?.[0];
        this._tempData.hotelName = decodeHTML(
          hotelInfo?.metadata?.tmsHotelCode?.description ||
            roomInfo?.metadata?.tmsHotelCode?.description ||
            roomInfo?.content?.hotelName ||
            "",
        );

        // Reset title/description from API data when EMMA ID changes
        const hotelContent = hotelInfo?.content || {};
        this._tempData.title = decodeHTML(
          hotelContent?.text?.content?.title || "",
        );
        this._tempData.description = decodeHTML(
          hotelContent?.text?.content?.shortDescription || "",
        );

        this._statusEl.innerHTML = this._tempData.hotelName
          ? `<strong>Loaded</strong>: ${this._tempData.hotelName}`
          : "Data loaded successfully.";

        this._buildSelectionUI(apiData);
      } else {
        this._statusEl.textContent = "Emma code not found, please try again";
        this._buildSelectionUI(apiData);
      }
    } catch (err) {
      this._statusEl.textContent = `Error: ${err.message}`;
      this._statusEl.classList.add("error");
    } finally {
      this._fetchBtn.disabled = false;
      this._fetchBtn.textContent = "Load data";
    }
  }

  async _fetchAllHotels() {
    const url = `${serverUrl}/api/v2/rhg/map?endpoint=hotel-info`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const apiData = await res.json();
    this._allHotels = apiData?.collection || [];
  }

  _buildSelectionUI(apiData) {
    const css = this._CSS;
    this._selectionArea.innerHTML = "";
    this._selectionArea.style.display = "block";
    this._checkboxes = [];

    const roomContent = apiData?.hotelRoomInfo?.collection?.[0]?.content || {};
    const hotelContent = apiData?.hotelInfo?.collection?.[0]?.content || {};

    // Numeric capacity data from hotelRoomInfo content
    const content = roomContent;

    // Services from hotelInfo.collection[0].content.service.values[]
    const serviceValues =
      hotelContent?.service?.values || apiData?.services || [];
    const services = serviceValues.map((s) => s?.service || s).filter(Boolean);

    // Previously selected keys for restoring state
    const prevKeys = new Set(this._data.selectedItems.map((i) => i.key));
    const hasExistingSelection = prevKeys.size > 0;

    // Count available numeric fields for default selection logic
    const availableNumericCount = NUMERIC_FIELDS.filter(
      (f) => content[f.key] != null,
    ).length;
    const defaultServiceSlots = MAX_SELECTED - availableNumericCount;

    // Numeric items
    const numHeading = document.createElement("div");
    numHeading.classList.add("at-a-glance-selection-heading");
    numHeading.classList.add(css.editorBlockModalLabel);

    const numList = document.createElement("ul");
    numList.classList.add("at-a-glance-selection-list");

    NUMERIC_FIELDS.forEach((field) => {
      const value = content[field.key];
      if (value == null) return;

      const checked = hasExistingSelection ? prevKeys.has(field.key) : true;
      const li = this._createSelectionItem({
        type: "numeric",
        key: field.key,
        label: field.label,
        displayLabel: field.displayLabel,
        value: value,
        checked,
      });
      numList.appendChild(li);
    });

    if (numList.children.length > 0) {
      numHeading.textContent = "Room information";
      this._selectionArea.appendChild(numHeading);
    }
    this._selectionArea.appendChild(numList);

    // Service items
    if (services.length > 0) {
      const svcHeading = document.createElement("div");
      svcHeading.classList.add(
        "at-a-glance-selection-heading",
        css.editorBlockModalLabel,
      );
      svcHeading.textContent = "Services";
      this._selectionArea.appendChild(svcHeading);

      const svcList = document.createElement("ul");
      svcList.classList.add("at-a-glance-selection-list");

      services.forEach((svc, idx) => {
        const name = decodeHTML(
          typeof svc === "string"
            ? svc
            : svc.description || svc.name || svc.label || String(svc),
        );
        const key =
          typeof svc === "string"
            ? svc
            : svc.key || svc.description || `service_${idx}`;
        const checked = hasExistingSelection
          ? prevKeys.has(key)
          : idx < defaultServiceSlots;

        const li = this._createSelectionItem({
          type: "service",
          key,
          label: name,
          checked,
        });
        svcList.appendChild(li);
      });

      this._selectionArea.appendChild(svcList);
    }

    this._updateCheckboxStates();
  }

  _createSelectionItem({ type, key, label, displayLabel, value, checked }) {
    const li = document.createElement("li");
    li.classList.add("at-a-glance-selection-item");

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = checked;
    cb.dataset.type = type;
    cb.dataset.key = key;
    cb.dataset.label = label;
    if (value != null) cb.dataset.value = value;

    cb.addEventListener("change", () => this._updateCheckboxStates());

    const labelEl = document.createElement("span");
    labelEl.classList.add("item-label");
    labelEl.textContent = displayLabel || label;

    li.appendChild(cb);
    li.appendChild(labelEl);

    if (type === "numeric" && value != null) {
      const valEl = document.createElement("span");
      valEl.classList.add("item-value");
      valEl.textContent = Number(value).toLocaleString();
      li.appendChild(valEl);
    }

    this._checkboxes.push({ cb, li });
    return li;
  }

  _getSelectedCount() {
    return this._checkboxes.filter(({ cb }) => cb.checked).length;
  }

  _updateCheckboxStates() {
    const count = this._getSelectedCount();

    this._checkboxes.forEach(({ cb, li }) => {
      if (!cb.checked && count >= MAX_SELECTED) {
        li.classList.add("disabled");
        cb.disabled = true;
      } else if (cb.checked && count <= MIN_SELECTED) {
        li.classList.add("disabled");
        cb.disabled = true;
      } else {
        li.classList.remove("disabled");
        cb.disabled = false;
      }
    });

    // Update apply button
    if (this._applyBtn) {
      this._applyBtn.disabled = count < MIN_SELECTED;
    }
  }

  _applySettings() {
    const hotelChanged = this._tempData.emmaId && this._tempData.emmaId !== this._data.emmaId;
    this._data = {
      ...this._data,
      ...this._tempData,
      imageOverrides: hotelChanged ? {} : this._data.imageOverrides,
    };
    const selected = this._checkboxes
      .filter(({ cb }) => cb.checked)
      .map(({ cb }) => {
        const item = {
          type: cb.dataset.type,
          key: cb.dataset.key,
          label: cb.dataset.label,
        };
        if (cb.dataset.type === "numeric" && cb.dataset.value != null) {
          item.value = Number(cb.dataset.value);
        }
        return item;
      });

    this._data.selectedItems = selected;
    this._hideModal();
    this._renderConfigured();

    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  _showModal() {
    if (this._modal) {
      this.hotelNameInput.value = this._data.hotelName;
      this._emmaInput.value = this._data.emmaId;

      // If we have cached API data, rebuild the selection UI
      if (this._data.apiData) {
        this._buildSelectionUI(this._data.apiData);
        this._statusEl.innerHTML = this._data.hotelName
          ? `<strong>Loaded</strong>: ${this._data.hotelName}`
          : "Data loaded.";
      } else {
        this._selectionArea.style.display = "none";
        this._selectionArea.innerHTML = "";
        this._statusEl.textContent = "";
      }

      this._modal.classList.add("show");
    }
  }

  _hideModal() {
    if (this._modal) {
      this._modal.classList.remove("show");
    }
  }

  save() {
    return {
      emmaId: this._data.emmaId,
      hotelName: this._data.hotelName,
      title: this._data.title,
      description: this._data.description,
      selectedItems: this._data.selectedItems,
      apiData: this._data.apiData,
      imageOverrides: this._data.imageOverrides,
      heroOpacity: this._data.heroOpacity,
    };
  }

  validate() {
    return true;
  }

  destroyed() {
    // No cleanup needed
  }
}
